import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import Container from '../ui/Container';
import Button from '../ui/Button';

const PlatformDemo = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const slidesRef = useRef<HTMLDivElement>(null);
  
  const slides = [
    {
      title: 'Find Drivers Nearby',
      description: 'Browse through available drivers in your area and view their profiles, ratings, and experience.',
      image: 'https://images.pexels.com/photos/35969/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      title: 'Book with Ease',
      description: 'Select your preferred driver, set your schedule, and book with just a few taps.',
      image: 'https://images.pexels.com/photos/7706486/pexels-photo-7706486.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      title: 'Track in Real-Time',
      description: 'Know exactly when your driver will arrive with live tracking and status updates.',
      image: 'https://images.pexels.com/photos/4386366/pexels-photo-4386366.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    }
  ];
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 5000);
    
    return () => clearInterval(interval);
  }, [slides.length]);
  
  const goToSlide = (index: number) => {
    setActiveSlide(index);
  };
  
  const goToPrevSlide = () => {
    setActiveSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const goToNextSlide = () => {
    setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <Container>
        <div ref={sectionRef} className="opacity-0 transition-opacity duration-1000">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Platform Demo</h2>
            <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See how easy it is to use DriversOnCall to book professional drivers for your transportation needs.
            </p>
          </div>
          
          <div className="relative">
            <div 
              ref={slidesRef} 
              className="relative rounded-xl overflow-hidden shadow-xl"
              style={{ height: '500px' }}
            >
              {slides.map((slide, index) => (
                <div 
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === activeSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"></div>
                  <img 
                    src={slide.image} 
                    alt={slide.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">{slide.title}</h3>
                    <p className="text-white/90 text-lg max-w-lg">{slide.description}</p>
                  </div>
                </div>
              ))}
              
              {/* Controls */}
              <button 
                onClick={goToPrevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <button 
                onClick={goToNextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-colors"
              >
                <ArrowRight size={20} />
              </button>
            </div>
            
            {/* Indicators */}
            <div className="flex justify-center mt-6 space-x-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === activeSlide ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                ></button>
              ))}
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <a 
              href="https://driversoncall.co" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="primary" size="lg">
                Try the Platform Now
              </Button>
            </a>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default PlatformDemo;